/**
 * Client-side active-context selection (Claude-style per-context switching).
 *
 * The user picks which account context they are acting in — `'personal'` (their
 * owned org, personal mode) or `org:<id>` (an org in team mode: their own org as
 * a team, or a team they hold a seat in). It is persisted PER CLIENT in
 * localStorage, so web and desktop each remember their OWN active context
 * independently — switching on one device does not propagate to another (this is
 * intended, mirroring how you can be in different workspaces on different
 * devices). The value is sent on every request as the `X-Active-Context` header;
 * the server AUTHORIZES it on every request and never trusts it.
 */

const STORAGE_KEY = 'kiao.activeCtx'

/** Header carrying the selected context; the server resolves + authorizes it. */
export const ACTIVE_CONTEXT_HEADER = 'X-Active-Context'

/** Default context: the user's personal account (their owned org, personal mode). */
export const DEFAULT_ACTIVE_CONTEXT = 'personal'

/**
 * `reason` tells a subscriber WHY the value changed, because the value alone is
 * ambiguous: clearing back to `'personal'` looks identical to explicitly
 * switching to `'personal'`, and seeding the store with the context the server
 * already picked looks identical to the user picking it by hand. Listeners that
 * don't care can omit the second parameter entirely.
 *
 *  - `'set'` — an explicit switch (`setActiveContext(value)`). Whatever a
 *    subscriber cached belongs to a DIFFERENT context: wrong, not merely stale.
 *  - `'adopt'` — `setActiveContext(value, { reason: 'adopt' })`, retained for
 *    callers that explicitly adopt a server-resolved context. The server's
 *    headerless fallback is now always personal, and personal adoption is
 *    deliberately skipped, so this reason normally does not write anything.
 *  - `'clear'` — `clearActiveContext()` with `notify` at its default. Only ever
 *    a still-authorized, mid-session recovery (403 `active_context_forbidden` /
 *    400 `invalid_active_context`), so any re-probe it triggers carries a live
 *    token. Sign-out clears with `{ notify: false }` and reaches NO listener at
 *    all — see `clearActiveContext`.
 *  - `'storage'` — another browser document changed the shared localStorage
 *    key. Consumers treat it as a hard workspace boundary; the web shell also
 *    returns to workspace home because the switch happened outside its own
 *    switcher callback.
 *
 * What each consumer does with them:
 *
 *  - `useVoiceQuota` — `'set'` and `'clear'` drop the cached usage and re-probe
 *    immediately; `'adopt'` KEEPS the usage and re-probes
 *    (stale-while-revalidate), so no window exists where `usage` is null. The
 *    immediate re-probe matters in all three cases because the server-driven
 *    runtime config rides on `usage` (sttMode, taigiVoiceEnabled,
 *    maxSentenceDurationSecs, defaultEnhancementMode) and nothing else restores
 *    it — including recoveries in the BACKGROUND (a global hotkey with no
 *    focus/visibilitychange to trigger a manual refresh).
 *  - `useOrganizationIdentity` — same for all three: re-read the key and
 *    refetch `/organizations/contexts`.
 *  - `useOverview` (desktop) — same for all three: refetch the overview.
 *  - `useActiveContext` — ignores the reason by construction: a
 *    `useSyncExternalStore` subscriber that only re-reads the snapshot and
 *    re-renders. No request.
 */
export type ActiveContextChangeReason = 'set' | 'adopt' | 'clear' | 'storage'
export type ActiveContextListener = (value: string, reason: ActiveContextChangeReason) => void

const listeners = new Set<ActiveContextListener>()
let isListeningForStorageChanges = false

// In-memory mirror so the value survives a session even where localStorage is
// unavailable/throws (private mode, SSR), and so reads never depend on a
// throwing storage call.
let memoryValue: string | null = null

type ActiveContextStorageEvent = Event & {
  key?: string | null
  newValue?: string | null
}

const storageEventTarget = globalThis as typeof globalThis & {
  addEventListener?: (type: string, listener: (event: Event) => void) => void
  removeEventListener?: (type: string, listener: (event: Event) => void) => void
}

function handleActiveContextStorageChange(event: Event): void {
  const storageEvent = event as ActiveContextStorageEvent
  if (storageEvent.key !== STORAGE_KEY || !('newValue' in storageEvent)) return

  memoryValue = storageEvent.newValue ?? null
  const value = memoryValue ?? DEFAULT_ACTIVE_CONTEXT
  for (const listener of listeners) listener(value, 'storage')
}

function startStorageChangeListener(): void {
  if (isListeningForStorageChanges || !storageEventTarget.addEventListener) return
  storageEventTarget.addEventListener('storage', handleActiveContextStorageChange)
  isListeningForStorageChanges = true
}

function readPersisted(): string | null {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) ?? null
  } catch {
    return null
  }
}

/** The current active-context value for this client. Defaults to `'personal'`. */
export function getActiveContext(): string {
  return memoryValue ?? readPersisted() ?? DEFAULT_ACTIVE_CONTEXT
}

/**
 * The value to send in `X-Active-Context`, or `null` to OMIT the header.
 *
 * Until the user EXPLICITLY picks a context we send NO header; the server's
 * only headerless fallback is personal. Once the user picks (or a switch is
 * persisted), the chosen value is always sent. This helper still distinguishes
 * an unset selection from an explicit `personal`, which is important when a
 * rejected selection is cleared and a request retries without a header.
 */
export function getActiveContextHeaderValue(): string | null {
  return hasStoredActiveContext() ? getActiveContext() : null
}

/**
 * Whether the user has EXPLICITLY chosen a context yet. False on first load;
 * both UI and server then resolve to personal without persisting a redundant key.
 */
export function hasStoredActiveContext(): boolean {
  return memoryValue !== null || readPersisted() !== null
}

/**
 * Clear the stored context (→ unset → personal fallback re-applies). Called by the
 * API client when the server rejects a stale/revoked context
 * (`active_context_forbidden`), so a user who lost a seat recovers automatically
 * instead of wedging on a 403 loop.
 *
 * `notify` (default `true`) controls whether subscribers are told about the
 * clear. Keep the default for the 403/400-recovery path — that is exactly what
 * `useVoiceQuota` etc. re-probe on, per the module doc above. Sign-out is the
 * one KIND of caller that must pass `{ notify: false }`: it fires on a session
 * whose token is ABOUT TO BE REMOVED (a 403 recovery, by contrast, fires while
 * the session stays authenticated and a re-probe is exactly what we want), so a
 * notification there would send every context-scoped subscriber
 * (`useVoiceQuota`, `useOrganizationIdentity`) probing right as the token is
 * being dropped — the probe races the token removal, either wastefully
 * carrying the about-to-be-discarded token or landing after removal and 401ing
 * for no reason. Both platforms have such a caller: web's `clearStoredToken()`
 * and desktop's `persistSession(null)`.
 *
 * Idempotent regardless of `notify`: if the store is ALREADY cleared (no
 * in-memory value AND nothing persisted), this is a no-op notification-wise —
 * there's nothing new to tell subscribers. Without this guard, a caller that
 * clears from an already-clear state (e.g. a second sign-out-adjacent call, or
 * a 403 recovery racing a sign-out) would still fire every subscriber for a
 * transition that never happened.
 */
export function clearActiveContext(options?: { notify?: boolean }): void {
  const notify = options?.notify ?? true
  const alreadyClear = memoryValue === null && readPersisted() === null
  memoryValue = null
  try {
    globalThis.localStorage?.removeItem(STORAGE_KEY)
  } catch {
    // best-effort
  }
  if (!notify || alreadyClear) return
  for (const listener of listeners) {
    listener(getActiveContext(), 'clear')
  }
}

/**
 * Select the active context for this client: persist it and notify subscribers
 * (e.g. so react-query refetches per-context data). Persistence is best-effort;
 * the in-memory mirror keeps the session correct even if storage throws.
 *
 * `options.reason` (default `'set'`) only affects what subscribers are TOLD;
 * persistence and dedup are identical either way. Pass `'adopt'` when seeding
 * the store with the context the server already resolved for headerless
 * requests, so subscribers revalidate in place instead of discarding data that
 * is still valid — see the `reason` doc above.
 *
 * Same-value writes are deduped at the notification step, but ONLY once a value
 * is already stored. Persistence always runs; the notification is skipped just
 * when a stored value is rewritten to itself. Without that dedup, writing the
 * server-resolved context back into the store on first load
 * (`useOrganizationIdentity`'s `adoptDefaultToStore`) — or a UI reselecting the
 * workspace that's already active — fires every context-scoped subscriber
 * (`useVoiceQuota`, `useOverview`, `useOrganizationIdentity` itself) as if the
 * user had switched workspaces, clearing cached data and re-probing for nothing.
 *
 * The `hasStoredActiveContext()` guard is what keeps that dedup from eating a
 * REAL change. With nothing stored, `getActiveContext()` reports the UI default
 * `'personal'` while the wire sends NO header at all — the server also resolves
 * personal. The first explicit `setActiveContext('personal')` still changes the
 * wire from an implicit to an explicit authority and notifies subscribers, so
 * client caches are revalidated against the now-pinned selection. Both reads
 * happen BEFORE the write, since persisting makes `hasStoredActiveContext()` true.
 *
 * The cost is one notification per first-load adoption on both platforms
 * (`adoptDefaultToStore` writes a previously-unstored default). Carrying
 * `reason: 'adopt'` keeps it cheap: a single revalidating probe, with no
 * subscriber blanking data the server was already serving from that very
 * context.
 */
/** Why `setActiveContext` is being called: an explicit user pick, or adopting the server-resolved default. */
export type ActiveContextWriteReason = 'set' | 'adopt'

export function setActiveContext(
  value: string,
  options?: { reason?: ActiveContextWriteReason }
): void {
  const reason = options?.reason ?? 'set'
  const hadStoredValue = hasStoredActiveContext()
  const previous = getActiveContext()
  memoryValue = value
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, value)
  } catch {
    // best-effort persistence — the in-memory mirror still reflects the choice
  }
  if (hadStoredValue && value === previous) return
  for (const listener of listeners) {
    listener(value, reason)
  }
}

/**
 * Subscribe to active-context changes. Returns an unsubscribe function. Use this
 * to invalidate/refetch context-scoped queries when the user switches.
 */
export function subscribeActiveContext(listener: ActiveContextListener): () => void {
  listeners.add(listener)
  // Keep the single process-wide storage listener alive after the first
  // subscriber. Otherwise a workspace switch in another tab can happen while
  // no React surface is mounted; memoryValue would then remain stale and take
  // precedence over localStorage when a scoped surface mounts again.
  startStorageChangeListener()
  return () => {
    listeners.delete(listener)
  }
}
